import axiosHttp from "services/axiosHttp";

/**
 * Sales Plan API.
 *
 * API_BASE_URL already ends in /backoffice/, so these paths sit
 * directly under it. Every endpoint scopes itself to the caller's
 * channel server-side — nothing here needs to pass a channel.
 */
const unwrap = (response) => response?.data?.data;

const salesPlanService = {
	// ---- master data -------------------------------------------------
	/** The metric columns this channel plans and enters. */
	getMetrics: async () => unwrap(await axiosHttp.get("sales-plan/metrics")),

	/** The Areas (routes) this channel can plan against. */
	getRoutes: async () => unwrap(await axiosHttp.get("sales-plan/routes")),

	getWeekTemplates: async () => unwrap(await axiosHttp.get("sales-plan/week-templates")),

	/**
	 * Owner search, backed by public.users — the same table
	 * sales_plan_line.owner_user_uuid references. Used to fill the
	 * Owner picker in the plan grid (Corporate rows like Razmin/Lakal
	 * are just users, same as anyone else).
	 */
	searchOwners: async (keyword = "") => {
		const params = new URLSearchParams({ page: 1, limit: 20, status: "true", keyword });
		const response = await axiosHttp.get(`user/list?${params}`);
		return response?.data?.data?.docs ?? [];
	},

	createWeekTemplate: async (payload) =>
		unwrap(await axiosHttp.post("sales-plan/week-templates", payload)),

	// ---- plans -------------------------------------------------------
	getPlans: async ({ year, month } = {}) => {
		const params = new URLSearchParams();
		if (year) params.append("year", year);
		if (month) params.append("month", month);
		return unwrap(await axiosHttp.get(`sales-plans?${params}`));
	},

	getPlan: async (uuid) => unwrap(await axiosHttp.get(`sales-plan/${uuid}`)),

	createPlan: async (payload) => unwrap(await axiosHttp.post("sales-plan", payload)),

	updatePlan: async (uuid, payload) => unwrap(await axiosHttp.put(`sales-plan/${uuid}`, payload)),

	setStatus: async (uuid, status) =>
		unwrap(await axiosHttp.put(`sales-plan/${uuid}/status`, { status })),

	saveLines: async (uuid, lines) =>
		unwrap(await axiosHttp.put(`sales-plan/${uuid}/lines`, { lines })),

	saveWeeks: async (uuid, payload) =>
		unwrap(await axiosHttp.put(`sales-plan/${uuid}/weeks`, payload)),

	/** Channel picker for the global admin's Channel Metrics screen. */
	getChannels: async () => unwrap(await axiosHttp.get("sales-plan/channels")),

	// ---- metric catalogue (master data admin) -------------------------
	getMetricCatalogue: async () => unwrap(await axiosHttp.get("sales-plan/metric-catalogue")),

	createMetric: async (payload) => unwrap(await axiosHttp.post("sales-plan/metric-catalogue", payload)),

	updateMetric: async (id, payload) =>
		unwrap(await axiosHttp.put(`sales-plan/metric-catalogue/${id}`, payload)),

	deactivateMetric: async (id) =>
		unwrap(await axiosHttp.delete(`sales-plan/metric-catalogue/${id}`)),

	// ---- channel metric configuration ---------------------------------
	getChannelMetricConfig: async (channelUuid) => {
		const params = channelUuid ? `?channel_uuid=${channelUuid}` : "";
		return unwrap(await axiosHttp.get(`sales-plan/channel-metrics${params}`));
	},

	saveChannelMetricConfig: async (channelUuid, rows) =>
		unwrap(
			await axiosHttp.put("sales-plan/channel-metrics", { channel_uuid: channelUuid, rows })
		),

	// ---- spreadsheet import -----------------------------------------
	/**
	 * Parses without writing unless commit is true, so the admin can
	 * review the column mapping and the unmatched Areas first.
	 */
	uploadPlan: async ({ file, year, month, commit = false, applyWeeklySplit = false }) => {
		const form = new FormData();
		form.append("file", file);
		form.append("year", year);
		form.append("month", month);
		form.append("commit", String(commit));
		form.append("apply_weekly_split", String(applyWeeklySplit));

		return unwrap(
			await axiosHttp.post("sales-plan/upload", form, {
				headers: { "Content-Type": "multipart/form-data" },
			})
		);
	},

	// ---- daily achievement -------------------------------------------
	getAchievements: async (uuid, date) =>
		unwrap(await axiosHttp.get(`sales-plan/${uuid}/achievements?date=${date}`)),

	saveAchievements: async (uuid, date, rows) =>
		unwrap(await axiosHttp.put(`sales-plan/${uuid}/achievements`, { date, rows })),
};

export default salesPlanService;
